import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getHealthFixedNavOffsetPx } from "./healthNavOffset";
import { getEffectiveLocationHash } from "../navigation/navPathUtils";

type HashNavigateTarget = {
    pathname: string;
    search: string;
    hash: string;
};

type HashNavigate = (
    to: HashNavigateTarget,
    options?: { replace?: boolean }
) => void;

const PAGE_TOP_HASH_CLEAR_THRESHOLD_PX = 80;
let suppressHashClearUntil = 0;

export function suppressPageHashClear(ms = 1200): void {
    suppressHashClearUntil = Date.now() + ms;
}

/** Remove the current hash without a full navigation. */
export function clearPageHashFromUrl(navigate?: HashNavigate): void {
    const { pathname, search, hash } = window.location;
    if (!hash) return;

    if (navigate) {
        navigate({ pathname, search, hash: "" }, { replace: true });
        return;
    }

    window.history.replaceState(null, "", `${pathname}${search}`);
}

/** Scroll landing hero to top and drop any in-page hash. */
export function scrollLandingPageToTop(navigate?: HashNavigate): void {
    clearPageHashFromUrl(navigate);
    window.scrollTo({ top: 0, behavior: "smooth" });
}

export function shouldClearHashNearPageTop(routerHash = ""): boolean {
    if (Date.now() < suppressHashClearUntil) {
        return false;
    }
    const hash = getEffectiveLocationHash(routerHash);
    if (!hash) {
        return false;
    }
    return window.scrollY <= PAGE_TOP_HASH_CLEAR_THRESHOLD_PX;
}

function getHashScrollTop(hash: string): number | null {
    if (!hash.startsWith("#")) return null;

    const id = hash.slice(1);
    if (!id) return null;

    const target = document.getElementById(id);
    if (!target) return null;

    const offsetPx = getHealthFixedNavOffsetPx();
    return Math.max(
        0,
        target.getBoundingClientRect().top + window.scrollY - offsetPx
    );
}

function alignToHealthHashTarget(
    hash: string,
    behavior: ScrollBehavior = "auto"
): boolean {
    const top = getHashScrollTop(hash);
    if (top === null) return false;

    window.scrollTo({ top, behavior });
    return true;
}

/** Re-run hash alignment after layout shifts (e.g. lifecycle scroll track height). */
export function realignToHealthHashAnchor(
    hash: string,
    behavior: ScrollBehavior = "auto"
): boolean {
    return alignToHealthHashTarget(hash, behavior);
}

/** Snap to exact offset once smooth scroll finishes (same position as reload). */
function finalizeHashScrollAfterSmooth(hash: string): void {
    const snap = () => alignToHealthHashTarget(hash, "auto");

    if ("onscrollend" in window) {
        window.addEventListener("scrollend", snap, { once: true });
        return;
    }

    window.setTimeout(snap, 500);
}

/** Scroll to a same-page hash target (e.g. `#agents`). */
export function scrollToHealthHashAnchor(
    hash: string,
    event?: { preventDefault: () => void },
    updateHistory = true,
    navigate?: HashNavigate
): boolean {
    if (!hash.startsWith("#")) return false;

    const id = hash.slice(1);
    if (!id) return false;

    const target = document.getElementById(id);
    if (!target) return false;

    event?.preventDefault();

    const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
        alignToHealthHashTarget(hash, "auto");
    } else {
        alignToHealthHashTarget(hash, "smooth");
        finalizeHashScrollAfterSmooth(hash);
    }

    if (updateHistory) {
        suppressPageHashClear();
        const { pathname, search } = window.location;
        if (navigate) {
            navigate({ pathname, search, hash }, { replace: true });
        } else {
            window.history.replaceState(null, "", `${pathname}${search}${hash}`);
        }
    }
    return true;
}

/** After route change, retry until the hash target exists in the DOM. */
export function scheduleScrollToHealthHashAnchor(
    hash: string,
    options?: { maxAttempts?: number; updateHistory?: boolean }
): () => void {
    if (!hash.startsWith("#")) {
        return () => {};
    }

    const maxAttempts = options?.maxAttempts ?? 16;
    const updateHistory = options?.updateHistory ?? false;
    let attempts = 0;
    let rafId = 0;
    let followUpTimeoutIds: number[] = [];

    const scheduleFollowUpRealign = () => {
        followUpTimeoutIds.forEach((id) => window.clearTimeout(id));
        // Lifecycle pinned track height is applied after first paint; re-align when layout settles.
        followUpTimeoutIds = [150, 400, 900].map((ms) =>
            window.setTimeout(() => {
                alignToHealthHashTarget(hash, "auto");
                suppressPageHashClear();
            }, ms)
        );
    };

    const tryScroll = () => {
        if (alignToHealthHashTarget(hash)) {
            suppressPageHashClear();
            if (updateHistory) {
                const { pathname, search } = window.location;
                window.history.replaceState(
                    null,
                    "",
                    `${pathname}${search}${hash}`
                );
            }
            scheduleFollowUpRealign();
            return;
        }

        attempts += 1;
        if (attempts < maxAttempts) {
            rafId = window.requestAnimationFrame(tryScroll);
        }
    };

    rafId = window.requestAnimationFrame(tryScroll);

    return () => {
        window.cancelAnimationFrame(rafId);
        followUpTimeoutIds.forEach((id) => window.clearTimeout(id));
    };
}

/** Drop in-page hash when the user scrolls back to the hero/top. */
export function useClearHashNearPageTop(enabled = true): void {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let ticking = false;

        const onScroll = () => {
            if (ticking) {
                return;
            }
            ticking = true;
            window.requestAnimationFrame(() => {
                ticking = false;
                if (shouldClearHashNearPageTop(location.hash)) {
                    clearPageHashFromUrl((to, options) => navigate(to, options));
                }
            });
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [enabled, navigate, location.hash]);
}
